import { Link } from "react-router-dom";
import { LogoMark } from "./Logo";
import { BUSINESS, SERVICE_LINKS } from "../lib/constants";
import { GOOGLE_READ_URL, GOOGLE_REVIEW_URL } from "./GoogleReviews";
import { Phone, Mail, MapPin, Clock, Globe, Facebook, Instagram } from "lucide-react";

const GoogleIcon = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12.48 10.92v3.28h7.84c-.24 1.84-.853 3.187-1.787 4.133-1.147 1.147-2.933 2.4-6.053 2.4-4.827 0-8.6-3.893-8.6-8.72s3.773-8.72 8.6-8.72c2.6 0 4.507 1.027 5.907 2.347l2.307-2.307C18.747 1.44 16.133 0 12.48 0 5.867 0 .307 5.387.307 12s5.56 12 12.173 12c3.573 0 6.267-1.173 8.373-3.36 2.16-2.16 2.84-5.213 2.84-7.667 0-.76-.053-1.467-.173-2.053H12.48z" />
  </svg>
);

const QUICK_LINKS = [
  { label: "Home", to: "/" },
  { label: "About", to: "/about" },
  { label: "Service Areas", to: "/service-areas" },
  { label: "FAQs", to: "/faqs" },
  { label: "Contact", to: "/contact" },
  { label: "Get an Estimate", to: "/get-estimate" },
];

export const Footer = () => (
  <footer data-testid="site-footer" className="bg-brand-mist/40 border-t border-brand-line/60">
    <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-20 pb-10">
      <div className="grid md:grid-cols-12 gap-12">
        <div className="md:col-span-4">
          <Link to="/" className="flex items-center gap-3" data-testid="footer-logo-link">
            <LogoMark size={44} />
            <span className="font-display font-semibold text-xl tracking-tight text-brand-ink">
              Kay's Carpet Cleaning
            </span>
          </Link>
          <p className="mt-5 text-sm text-brand-muted leading-relaxed max-w-xs">
            Professional carpet and cleaning services across the Gold Coast and
            selected surrounding areas. More than 7 years of hands-on experience and
            thousands of completed cleaning jobs.
          </p>
          <div className="mt-7">
            <h3 className="text-xs font-bold tracking-[0.18em] uppercase text-brand-ink">Follow & Review</h3>
            <div className="mt-4 flex items-center gap-3">
              <a href={GOOGLE_READ_URL} target="_blank" rel="noopener noreferrer" aria-label="Google Reviews for Kay's Carpet Cleaning (opens in a new tab)" data-testid="footer-google-reviews-link" className="w-11 h-11 rounded-full bg-white border border-brand-line/70 flex items-center justify-center text-brand hover:border-brand hover:bg-brand-mist/60 transition-colors duration-300">
                <GoogleIcon />
              </a>
              <a href="https://www.facebook.com/share/1BhqWHiEF1/" target="_blank" rel="noopener noreferrer" aria-label="Kay's Carpet Cleaning on Facebook (opens in a new tab)" data-testid="footer-facebook-link" className="w-11 h-11 rounded-full bg-white border border-brand-line/70 flex items-center justify-center text-brand hover:border-brand hover:bg-brand-mist/60 transition-colors duration-300">
                <Facebook size={17} />
              </a>
              <a href="https://www.instagram.com/kayscarpetcleaning" target="_blank" rel="noopener noreferrer" aria-label="Kay's Carpet Cleaning on Instagram (opens in a new tab)" data-testid="footer-instagram-link" className="w-11 h-11 rounded-full bg-white border border-brand-line/70 flex items-center justify-center text-brand hover:border-brand hover:bg-brand-mist/60 transition-colors duration-300">
                <Instagram size={17} />
              </a>
            </div>
            <div className="mt-4 flex flex-wrap gap-x-5 gap-y-1.5 text-sm">
              <a href={GOOGLE_READ_URL} target="_blank" rel="noopener noreferrer" data-testid="footer-google-reviews-text-link" className="font-semibold text-brand hover:text-brand-dark underline underline-offset-2 transition-colors duration-300">Google Reviews</a>
              <a href={GOOGLE_REVIEW_URL} target="_blank" rel="noopener noreferrer" data-testid="footer-leave-review-link" className="font-semibold text-brand hover:text-brand-dark underline underline-offset-2 transition-colors duration-300">Leave a Review</a>
            </div>
          </div>

          <div className="mt-6">
            <h3 className="text-xs font-bold tracking-[0.18em] uppercase text-brand-ink">Business Information</h3>
            <p className="mt-2 text-xs font-bold tracking-[0.14em] uppercase text-brand-muted" data-testid="footer-abn">
              ABN {BUSINESS.abn}
            </p>
          </div>
        </div>

        <div className="md:col-span-2">
          <h3 className="text-xs font-bold tracking-[0.18em] uppercase text-brand-ink">Quick Links</h3>
          <ul className="mt-5 space-y-3">
            {QUICK_LINKS.map((l) => (
              <li key={l.to}>
                <Link to={l.to} data-testid={`footer-link-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="text-sm text-brand-muted hover:text-brand transition-colors duration-300">
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="md:col-span-3">
          <h3 className="text-xs font-bold tracking-[0.18em] uppercase text-brand-ink">Services</h3>
          <ul className="mt-5 space-y-3">
            {SERVICE_LINKS.map((s) => (
              <li key={s.to}>
                <Link to={s.to} data-testid={`footer-service-${s.to.slice(1)}`} className="text-sm text-brand-muted hover:text-brand transition-colors duration-300">
                  {s.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="md:col-span-3">
          <h3 className="text-xs font-bold tracking-[0.18em] uppercase text-brand-ink">Contact</h3>
          <ul className="mt-5 space-y-4 text-sm text-brand-muted">
            <li>
              <a href={BUSINESS.phoneHref} data-testid="footer-phone-link" className="flex items-center gap-2.5 hover:text-brand transition-colors duration-300">
                <Phone size={15} className="text-brand shrink-0" /> {BUSINESS.phoneDisplay}
              </a>
            </li>
            <li>
              <a href={BUSINESS.emailHref} data-testid="footer-email-link" className="flex items-center gap-2.5 hover:text-brand transition-colors duration-300 break-all">
                <Mail size={15} className="text-brand shrink-0" /> {BUSINESS.email}
              </a>
            </li>
            <li className="flex items-center gap-2.5">
              <MapPin size={15} className="text-brand shrink-0" /> Gold Coast, Queensland
            </li>
            <li className="flex items-center gap-2.5" data-testid="footer-website">
              <Globe size={15} className="text-brand shrink-0" /> kayscarpetcleaning.com.au
            </li>
            <li data-testid="footer-hours">
              <h4 className="text-xs font-bold tracking-[0.14em] uppercase text-brand-ink mb-1.5">Hours</h4>
              <span className="flex items-start gap-2.5">
                <Clock size={15} className="text-brand shrink-0 mt-0.5" />
                <span>Monday – Saturday<br />8:00 AM – 8:00 PM</span>
              </span>
            </li>
          </ul>
        </div>
      </div>

      <div className="mt-16 pt-8 border-t border-brand-line/70 flex flex-col sm:flex-row items-center justify-between gap-3">
        <p className="text-xs text-brand-muted" data-testid="footer-copyright">
          © 2026 Kay's Carpet Cleaning. All Rights Reserved.
        </p>
        <p className="text-xs text-brand-muted">
          Website by{" "}
          <a
            href="https://ozaihub.com/"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="footer-ozaihub-link"
            className="font-semibold text-brand hover:text-brand-dark underline underline-offset-2 transition-colors duration-300"
          >
            OzAIHub
          </a>
        </p>
      </div>
    </div>
  </footer>
);
